import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Truck, Bike, UtensilsCrossed, Car as CarIcon, ShoppingBag, Package, Users, Repeat, Clock, Lock, Loader2, Plus, Trash2, MessageCircle, Phone as PhoneIcon, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { gatePassAPI } from '../../services/api';

export const PASS_TYPES = [
  { id: 'quick', label: 'Quick Pass', desc: 'One-time visitor', icon: Users, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { id: 'delivery', label: 'Delivery', desc: 'Food, courier, cab, groceries', icon: Truck, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { id: 'frequent', label: 'Frequent Visitor', desc: 'Days / weeks / months', icon: Repeat, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { id: 'party', label: 'Party Invite', desc: 'Up to 20 guests', icon: Users, color: 'bg-pink-50 text-pink-700 border-pink-200' },
];

const DELIVERY_CATEGORIES = [
  { id: 'food', label: 'Food Delivery', icon: UtensilsCrossed, placeholder: 'e.g., Swiggy, Zomato' },
  { id: 'courier', label: 'Courier', icon: Package, placeholder: 'e.g., DTDC, Amazon' },
  { id: 'cab', label: 'Cab', icon: CarIcon, placeholder: 'e.g., Uber, Yellow Taxi' },
  { id: 'groceries', label: 'Groceries / Essentials', icon: ShoppingBag, placeholder: 'e.g., BigBasket, Flipkart' },
  { id: 'other', label: 'Other', icon: Bike, placeholder: 'Custom description' },
];

const FREQUENT_PRESETS = [
  { id: '7', label: '1 Week', days: 7 },
  { id: '30', label: '1 Month', days: 30 },
  { id: 'custom', label: 'Custom (max 45 days)', days: null },
];

function PrivateToggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      data-testid="pass-private-toggle"
      className={`flex items-center gap-2 w-full p-3 rounded-lg border-2 transition-colors ${
        value ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 text-slate-700 border-slate-200'
      }`}
    >
      <Lock className="w-4 h-4" />
      <div className="text-left flex-1">
        <p className="text-sm font-semibold">Private pass</p>
        <p className="text-[11px] opacity-80">Only you (the issuer) get notified — other accounts on this villa won't.</p>
      </div>
      <div className={`w-10 h-6 rounded-full relative transition-colors ${value ? 'bg-emerald-400' : 'bg-slate-300'}`}>
        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${value ? 'left-5' : 'left-1'}`} />
      </div>
    </button>
  );
}

// --- Main Picker ---
export function GatePassPickerModal({ open, onOpenChange, onSelect }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="gate-pass-picker-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Gate Pass</DialogTitle>
          <DialogDescription>Choose a pass type that fits your visitor</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          {PASS_TYPES.map(p => (
            <button
              key={p.id}
              data-testid={`pick-pass-${p.id}`}
              onClick={() => onSelect(p.id)}
              className={`p-4 rounded-xl border-2 text-left hover:scale-[1.02] transition-transform ${p.color}`}
            >
              <p.icon className="w-6 h-6 mb-2" />
              <p className="font-bold text-sm">{p.label}</p>
              <p className="text-[11px] opacity-80 mt-0.5">{p.desc}</p>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Quick Pass (existing flow) ---
export function QuickPassModal({ open, onOpenChange, villaNumber, onCreated }) {
  const [form, setForm] = useState({ visitor_name: '', visitor_phone: '', valid_hours: 4, is_private: false });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.visitor_name.trim()) return toast.error('Visitor name is required');
    setSubmitting(true);
    try {
      const { data } = await gatePassAPI.create({
        visitor_name: form.visitor_name.trim(),
        visitor_phone: form.visitor_phone.trim() || null,
        villa_number: villaNumber,
        valid_hours: parseInt(form.valid_hours, 10),
        pass_category: 'quick',
        is_private: form.is_private,
      });
      toast.success('Gate pass created');
      onCreated?.(data);
      onOpenChange(false);
      setForm({ visitor_name: '', visitor_phone: '', valid_hours: 4, is_private: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create pass');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quick-pass-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Quick Pass</DialogTitle>
          <DialogDescription>Pre-approve a one-time visitor</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Visitor Name</Label>
            <Input data-testid="quick-visitor-name" value={form.visitor_name} onChange={e => setForm({ ...form, visitor_name: e.target.value })} />
          </div>
          <div>
            <Label>Visitor Phone</Label>
            <Input data-testid="quick-visitor-phone" value={form.visitor_phone} onChange={e => setForm({ ...form, visitor_phone: e.target.value })} />
          </div>
          <div>
            <Label>Valid Hours</Label>
            <select data-testid="quick-valid-hours" value={form.valid_hours} onChange={e => setForm({ ...form, valid_hours: e.target.value })} className="w-full p-2 border rounded-md">
              {[1, 2, 4, 8, 12, 24].map(h => <option key={h} value={h}>{h} hour{h > 1 ? 's' : ''}</option>)}
            </select>
          </div>
          <PrivateToggle value={form.is_private} onChange={v => setForm({ ...form, is_private: v })} />
          <Button data-testid="quick-pass-submit" onClick={submit} disabled={submitting} className="w-full bg-gk-teal hover:bg-gk-teal-hover">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Generate Pass
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Delivery Pass ---
export function DeliveryPassModal({ open, onOpenChange, villaNumber, onCreated }) {
  const [category, setCategory] = useState('food');
  const [providerName, setProviderName] = useState('');
  const [validHours, setValidHours] = useState(2);
  const [isPrivate, setIsPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!providerName.trim()) return toast.error('Provider/visitor name is required');
    setSubmitting(true);
    try {
      const { data } = await gatePassAPI.create({
        visitor_name: providerName.trim(),
        visitor_phone: null,
        villa_number: villaNumber,
        valid_hours: parseInt(validHours, 10),
        pass_category: 'delivery',
        delivery_category: category,
        delivery_provider: providerName.trim(),
        is_private: isPrivate,
      });
      toast.success('Delivery pre-approval created');
      onCreated?.(data);
      onOpenChange(false);
      setProviderName(''); setValidHours(2); setIsPrivate(false); setCategory('food');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally { setSubmitting(false); }
  };

  const cat = DELIVERY_CATEGORIES.find(c => c.id === category);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delivery-pass-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delivery Pre-Approval</DialogTitle>
          <DialogDescription>Pre-approve a delivery for fast gate entry</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Category</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {DELIVERY_CATEGORIES.map(c => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`delivery-cat-${c.id}`}
                  onClick={() => setCategory(c.id)}
                  className={`p-2 rounded-lg border-2 flex flex-col items-center gap-1 ${category === c.id ? 'border-gk-teal bg-gk-teal/10 text-gk-teal' : 'border-slate-200 text-slate-600'}`}
                >
                  <c.icon className="w-4 h-4" />
                  <span className="text-[10px] leading-tight text-center">{c.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Provider / Service Name</Label>
            <Input
              data-testid="delivery-provider-input"
              value={providerName}
              onChange={e => setProviderName(e.target.value)}
              placeholder={cat?.placeholder || ''}
            />
          </div>
          <div>
            <Label>Valid for (hours): {validHours}h</Label>
            <input
              data-testid="delivery-hours-slider"
              type="range" min={1} max={24} value={validHours}
              onChange={e => setValidHours(e.target.value)}
              className="w-full accent-gk-teal"
            />
            <div className="flex justify-between text-[10px] text-slate-400">
              <span>1h</span><span>24h</span>
            </div>
          </div>
          <PrivateToggle value={isPrivate} onChange={setIsPrivate} />
          <Button data-testid="delivery-submit" onClick={submit} disabled={submitting} className="w-full bg-gk-teal hover:bg-gk-teal-hover">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Create Delivery Pass
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Frequent Visitor Pass ---
export function FrequentPassModal({ open, onOpenChange, villaNumber, onCreated }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [preset, setPreset] = useState('7');
  const [endDate, setEndDate] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error('Visitor name is required');

    const payload = {
      visitor_name: name.trim(),
      visitor_phone: phone.trim() || null,
      villa_number: villaNumber,
      pass_category: 'frequent',
      multi_use: true,
      is_private: isPrivate,
      valid_hours: 24 * 7, // default
    };

    if (preset === 'custom') {
      if (!endDate) return toast.error('Please pick an end date');
      const end = new Date(endDate);
      const now = new Date();
      const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      if (days <= 0) return toast.error('End date must be in the future');
      if (days > 45) return toast.error('Maximum 45 days');
      payload.valid_until_iso = end.toISOString();
    } else {
      const days = parseInt(preset, 10);
      payload.valid_hours = days * 24;
    }

    setSubmitting(true);
    try {
      const { data } = await gatePassAPI.create(payload);
      toast.success('Frequent visitor pass created');
      onCreated?.(data);
      onOpenChange(false);
      setName(''); setPhone(''); setPreset('7'); setEndDate(''); setIsPrivate(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally { setSubmitting(false); }
  };

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 45);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="frequent-pass-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Frequent Visitor Pass</DialogTitle>
          <DialogDescription>Issue a 6-digit code that works repeatedly within the validity</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Visitor Name</Label>
            <Input data-testid="frequent-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Maria — house help" />
          </div>
          <div>
            <Label>Visitor Phone</Label>
            <Input data-testid="frequent-phone" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Optional" />
          </div>
          <div>
            <Label>Validity</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {FREQUENT_PRESETS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`frequent-preset-${p.id}`}
                  onClick={() => setPreset(p.id)}
                  className={`p-2 rounded-lg border-2 text-xs ${preset === p.id ? 'border-gk-teal bg-gk-teal/10 text-gk-teal font-semibold' : 'border-slate-200 text-slate-600'}`}
                >{p.label}</button>
              ))}
            </div>
          </div>
          {preset === 'custom' && (
            <div>
              <Label>End Date</Label>
              <Input
                data-testid="frequent-end-date"
                type="date"
                value={endDate}
                min={minDateStr}
                max={maxDateStr}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
          )}
          <PrivateToggle value={isPrivate} onChange={setIsPrivate} />
          <Button data-testid="frequent-submit" onClick={submit} disabled={submitting} className="w-full bg-gk-teal hover:bg-gk-teal-hover">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Generate 6-digit Pass
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Party Invite ---
export function PartyInviteModal({ open, onOpenChange, villaNumber, onCreated }) {
  const [guests, setGuests] = useState([{ name: '', phone: '' }]);
  const [hours, setHours] = useState(8);
  const [isPrivate, setIsPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdPasses, setCreatedPasses] = useState(null);

  const addGuest = () => {
    if (guests.length >= 20) return toast.error('Maximum 20 guests');
    setGuests([...guests, { name: '', phone: '' }]);
  };
  const removeGuest = (idx) => setGuests(guests.filter((_, i) => i !== idx));
  const updateGuest = (idx, key, val) => {
    setGuests(guests.map((g, i) => i === idx ? { ...g, [key]: val } : g));
  };

  const submit = async () => {
    const valid = guests.filter(g => g.name && g.name.trim());
    if (valid.length === 0) return toast.error('Add at least one guest');
    setSubmitting(true);
    try {
      const { data } = await gatePassAPI.createParty({
        villa_number: villaNumber,
        valid_hours: parseInt(hours, 10),
        is_private: isPrivate,
        guests: valid.map(g => ({ name: g.name.trim(), phone: (g.phone || '').trim() || null })),
      });
      toast.success(`${data.passes.length} party passes created`);
      setCreatedPasses(data.passes);
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally { setSubmitting(false); }
  };

  const reset = () => {
    setGuests([{ name: '', phone: '' }]);
    setHours(8); setIsPrivate(false); setCreatedPasses(null);
  };
  const close = () => { reset(); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="party-invite-modal" className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Party Invite</DialogTitle>
          <DialogDescription>{createdPasses ? 'Share invites with each guest' : 'Add up to 20 guests — one pass each'}</DialogDescription>
        </DialogHeader>

        {createdPasses ? (
          <PartyInviteResult
            passes={createdPasses}
            villaNumber={villaNumber}
            onClose={close}
          />
        ) : (
          <div className="space-y-3 py-2">
            <div>
              <Label>Valid for (hours)</Label>
              <select data-testid="party-hours" value={hours} onChange={e => setHours(e.target.value)} className="w-full p-2 border rounded-md">
                {[2, 4, 6, 8, 12, 24].map(h => <option key={h} value={h}>{h} hours</option>)}
              </select>
            </div>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
              <Label>Guests ({guests.length}/20)</Label>
              {guests.map((g, idx) => (
                <div key={idx} className="flex gap-2 items-start" data-testid={`party-guest-row-${idx}`}>
                  <Input
                    data-testid={`party-guest-name-${idx}`}
                    value={g.name}
                    onChange={e => updateGuest(idx, 'name', e.target.value)}
                    placeholder="Name"
                    className="flex-1"
                  />
                  <Input
                    data-testid={`party-guest-phone-${idx}`}
                    value={g.phone}
                    onChange={e => updateGuest(idx, 'phone', e.target.value)}
                    placeholder="Phone"
                    className="flex-1"
                  />
                  {guests.length > 1 && (
                    <Button data-testid={`party-guest-remove-${idx}`} type="button" size="sm" variant="ghost" onClick={() => removeGuest(idx)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              {guests.length < 20 && (
                <Button data-testid="party-add-guest" type="button" size="sm" variant="outline" onClick={addGuest} className="w-full">
                  <Plus className="w-4 h-4 mr-1" /> Add another guest
                </Button>
              )}
            </div>
            <PrivateToggle value={isPrivate} onChange={setIsPrivate} />
            <Button data-testid="party-submit" onClick={submit} disabled={submitting} className="w-full bg-gk-teal hover:bg-gk-teal-hover">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create {guests.filter(g => g.name && g.name.trim()).length || 0} Passes
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PartyInviteResult({ passes, villaNumber, onClose }) {
  const [copiedId, setCopiedId] = useState(null);

  const copyInvite = async (p) => {
    const msg = `You're invited to my villa! Use code ${p.access_code} at the gate. Valid until ${new Date(p.valid_until).toLocaleString()}. – Villa ${villaNumber}`;
    try {
      await navigator.clipboard.writeText(msg);
      setCopiedId(p.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const messageGuest = (p) => {
    const text = `Hi ${p.visitor_name}, you're invited! Gate code: ${p.access_code}. Valid until ${new Date(p.valid_until).toLocaleString()}. – Villa ${villaNumber}`;
    if (p.visitor_phone) {
      const wa = `https://wa.me/${(p.visitor_phone || '').replace(/[^\d]/g, '')}?text=${encodeURIComponent(text)}`;
      window.open(wa, '_blank');
    } else {
      const sms = `sms:?body=${encodeURIComponent(text)}`;
      window.location.href = sms;
    }
  };

  return (
    <div className="space-y-2 py-2">
      <Badge variant="outline" className="mb-2">{passes.length} passes generated</Badge>
      <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
        {passes.map((p) => (
          <div key={p.id} data-testid={`party-pass-${p.id}`} className="p-3 bg-pink-50 border border-pink-200 rounded-lg">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-gk-navy truncate">{p.visitor_name}</p>
                {p.visitor_phone && <p className="text-xs text-slate-500">{p.visitor_phone}</p>}
                <p className="font-mono text-xl font-bold text-pink-600 mt-1">{p.access_code}</p>
              </div>
              <div className="flex flex-col gap-1">
                {p.visitor_phone && (
                  <a
                    data-testid={`party-call-${p.id}`}
                    href={`tel:${p.visitor_phone}`}
                    className="inline-flex items-center gap-1 text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-700 px-2 py-1 rounded"
                  >
                    <PhoneIcon className="w-3 h-3" /> Call
                  </a>
                )}
                <button
                  data-testid={`party-message-${p.id}`}
                  onClick={() => messageGuest(p)}
                  className="inline-flex items-center gap-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded"
                >
                  <MessageCircle className="w-3 h-3" /> Message
                </button>
                <button
                  data-testid={`party-copy-${p.id}`}
                  onClick={() => copyInvite(p)}
                  className="inline-flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded"
                >
                  {copiedId === p.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copiedId === p.id ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Button data-testid="party-result-close" onClick={onClose} className="w-full bg-gk-teal hover:bg-gk-teal-hover">Done</Button>
    </div>
  );
}
